import React, {FC} from "react"
import styled, {css} from "styled-components"
import {H3, H4} from "@irati/ui/src/Typography"
import {RouterLink} from "@irati/ui/src/Link"

interface ProductCardProps {
  name: string
  image: string
  price: number
  term: number
  productTagImage: string
}

const ProductCard: FC<ProductCardProps> = ({name, image, price, term, productTagImage}) => {
  return (
    // temporary route to hardcoded product id
    <RouterLink to={"/products/1"}>
      <OuterContainer>
        <InnerContainer isResponsive>
          <CardImgContainer isResponsive>
            <CardImg src={image} alt="product image" />
          </CardImgContainer>
          <CardContent isResponsive>
            <TitleContainer isResponsive>
              <Title isResponsive>{name}</Title>
            </TitleContainer>
            <Price>£{price.toFixed(2)}</Price>
            <PriceTagLine>per month</PriceTagLine>
            <PriceTagLine>{`${term} month term`}</PriceTagLine>
          </CardContent>
          {productTagImage && (
            <FlashDealTag isResponsive>
              <FlashDealImg src={productTagImage} alt="flash deal image" />
            </FlashDealTag>
          )}
        </InnerContainer>
      </OuterContainer>
    </RouterLink>
  )
}

export default ProductCard

const OuterContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

interface InnerContainerProps {
  isResponsive?: boolean
  showAddToOrder?: boolean
  isTabPressed?: boolean
}

const InnerContainer = styled.div<InnerContainerProps>`
  position: relative;
  width: 100%;
  max-width: 254px;
  margin-right: 16px;
  height: 354px;
  border-radius: 16px;
  box-shadow: 0px 3px 6px #00000029;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease-in-out;
  align-items: flex-end;
  text-decoration: none;
  background-color: ${({theme}) => theme.colors.greys.white};

  &:hover {
    transform: scale(1.03);
  }

  ${({isResponsive}) =>
    isResponsive &&
    css`
      width: 254px;
      ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
        align-items: center;
        width: 167px;
        height: 300px;
        margin: 0;
      `}
    `}

  ${({showAddToOrder}) =>
    showAddToOrder &&
    css`
      height: 425px;
    `}

  ${({showAddToOrder, isResponsive}) =>
    showAddToOrder &&
    isResponsive &&
    css`
      ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
        height: 360px;
      `}
    `}

  ${(props) =>
    props.isTabPressed &&
    css`
      transform: scale(1.03);
    `}
`

interface CardImgContainerProps {
  isResponsive?: boolean
  showAddToOrder?: boolean
}

const CardImgContainer = styled.div<CardImgContainerProps>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({showAddToOrder}) => (showAddToOrder ? "50%" : "55%")};

  ${({isResponsive, theme}) =>
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        height: 40%;
      `}
    `}
`

const CardImg = styled.img`
  max-height: 85%;
  max-width: 85%;
  object-fit: fill;

  @media (-ms-high-contrast: none), (-ms-high-contrast: active) {
    max-width: 210px;
    max-height: 167px;
  }
`

interface CardContentProps {
  showAddToOrder?: boolean
  isResponsive?: boolean
}

const CardContent = styled.div<CardContentProps>`
  padding: 8px 0;
  width: 85%;
  align-self: center;
  height: ${({showAddToOrder}) => (showAddToOrder ? "208px" : "137.6px")};

  ${({showAddToOrder, isResponsive, theme}) =>
    !showAddToOrder &&
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        height: 156.8px;
      `}
    `}
`

interface TitleProps {
  isResponsive?: boolean
}

interface TitleContainerProps {
  isResponsive?: boolean
}

const TitleContainer = styled.div<TitleContainerProps>`
  height: 50px;
  margin-bottom: 12px;

  ${({isResponsive, theme}) =>
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        height: 60px;
        margin-bottom: 15px;
      `}
    `}
`

const Title = styled(H3)<TitleProps>`
  line-height: 16px;
  display: box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  color: ${({theme}) => theme.colors.greys.black};

  ${({isResponsive, theme}) =>
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        font-weight: 400;
        display: -webkit-box;
        font-size: 12px;
        color: ${theme.colors.greys.black};
      `}
    `}

  font-weight: bold !important;
`

const Price = styled(H3)`
  font-size: 24px;
  font-weight: bold;
  line-height: 16px;
  margin: 4px 0 8px 0;
  color: ${({theme}) => theme.colors.primary1};
`

interface PriceTagLineProps {
  isResponsive?: boolean
}

const PriceTagLine = styled(H4)<PriceTagLineProps>`
  line-height: 16px;
  text-align: left;
  color: ${({theme}) => theme.colors.greys.black};
  font-size: 14px;
  margin-bottom: 6px;

  ${({isResponsive, theme}) =>
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        font-size: 12px;
      `}
    `}
`

interface FlashDealTagProps {
  isResponsive?: boolean
}

const FlashDealTag = styled.div<FlashDealTagProps>`
  position: absolute;
  z-index: 9;
  /* box-shadow: 0px 3px 6px; */

  ${({theme}) => theme.mediaQueries.minWidthMedia.phoneLandscape`
    width: 76px;
    height: 76px;
    top: 17%;
    left: 75%;
    transform: translate(-10%,-95%);
  `}

  ${({theme}) => theme.mediaQueries.minWidthMedia.extraLargeDesktop`
    width: 76px;
    height: 76px;
    top: 17%;
    left: 76%;
  `}

  ${({isResponsive, theme}) =>
    isResponsive &&
    css`
      ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
        width: 55px;
        height: 55px;
        top: -4%;
        left: 70%;
      `}
    `}
`

const FlashDealImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill;
`
